/*
 * @Author: Ryan
 * @Date: 2026-01-14
 * @Description: Frequently Asked Questions section with expandable items
 */
import React, { useState } from 'react';

interface FAQItem {
  id: string;
  question: string;
  answer: string | React.ReactNode;
}

interface FAQItemProps {
  item: FAQItem;
  isExpanded: boolean;
  onClick: () => void;
}

const FAQItemComponent: React.FC<FAQItemProps> = ({ item, isExpanded, onClick }) => {
  return (
    <div
      className={`
        border border-[#CDCDCD] transition-all duration-300 cursor-pointer
        ${isExpanded ? 'bg-white' : 'bg-white hover:bg-gray-50'}
      `}
      onClick={onClick}
    >
      <div className="px-6 py-6 pad:px-8 pad:py-8">
        {/* 问题行 */}
        <div className="flex items-center justify-between gap-4">
          {/* 问题文本 */}
          <h3 className="flex-1 text-base pad:text-base font-semibold text-[#0F1117]">
            {item.question}
          </h3>

          {/* +/- 图标 */}
          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
            {isExpanded ? (
              <svg
                className="w-5 h-5 text-[#0F1117]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 12H4"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5 text-[#0F1117]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            )}
          </div>
        </div>

        {/* 答案 - 展开时显示 */}
        <div
          className={`
            overflow-hidden transition-all duration-300
            ${isExpanded ? 'max-h-[500px] opacity-100 mt-4' : 'max-h-0 opacity-0'}
          `}
        >
          <div className="text-sm pad:text-base text-gray-600 leading-relaxed">
            {item.answer}
          </div>
        </div>
      </div>
    </div>
  );
};

const FAQSection: React.FC = () => {
  // 使用 Set 来跟踪展开的问题 ID，允许同时展开多个
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set(['faq-1']));

  const faqItems: FAQItem[] = [
    {
      id: 'faq-1',
      question: 'How to Use FalcoCut AI Video Generator?',
      answer: (
        <>
          FalcoCut turns your ideas into videos through a simple, guided workflow.
          <br />
          <br />
          <strong>Step 1:</strong> You choose your input type, such as text or an image.
          <br />
          <br />
          <strong>Step 2:</strong> You enter your prompt or upload your visual.
          <br />
          <br />
          <strong>Step 3:</strong> You pick the video length and resolution. After that,
          FalcoCut processes your request and generates a video based on your
          instructions.
          <br />
          <br />
          <strong>Step 4:</strong> Finally, you preview the result and download it or
          create more variations.
          <br />
          <br />
          The whole process is designed to be quick, clear, and beginner-friendly.
        </>
      ),
    },
    {
      id: 'faq-2',
      question: 'What types of videos can I create with FalcoCut?',
      answer:
        'FalcoCut can create a wide range of short videos for both personal and professional use. You can make product demos, e-commerce ads, UGC-style social videos, and quick promo clips for platforms like TikTok, Instagram, and YouTube. It also works well for animated scenes, concept previews, character-based stories, and simple explainers. Many users create training videos, classroom visuals, and marketing assets without filming anything. You can even turn images into motion clips or bring characters to life for creative projects. Because FalcoCut supports different input types and styles, it fits workflows in marketing, education, design, e-commerce, and everyday content creation.',
    },
    {
      id: 'faq-3',
      question: 'What is the best AI video generator?',
      answer:
        "FalcoCut is the best AI video generator for creators who want a fast, simple, and reliable way to turn ideas into polished videos. Compared with tools like Runway, Pika, InVideo, CapCut, and Canva, FalcoCut offers a smoother workflow and clearer results without complicated settings. It works with text, images, and characters, making it a strong choice for product showcases, promotional clips, customer-story videos, training visuals, educational explainers, and short creative scenes. Whether you're in marketing, education, design, or small-business content creation, FalcoCut helps you produce high-quality videos in minutes.",
    },
    {
      id: 'faq-4',
      question: 'Which AI video model is best for beginners?',
      answer:
        "For beginners, the best AI video model is one that produces clean, stable results with very little setup. Models like Sora, Veo, and Kling are known for handling motion, lighting, and scene interpretation automatically, which helps new users get great results with simple prompts. FalcoCut includes these beginner-friendly models, so you can create smooth, high-quality videos even if you've never used an AI video tool before.",
    },
    {
      id: 'faq-5',
      question: 'What types of input can I use to create videos?',
      answer:
        "FalcoCut supports multiple input types so you can start your video in the way that feels most natural. You can enter text to describe a scene, upload an image to animate it, or use character assets you've created. These inputs help the AI understand your idea and build a video that matches your style. You can mix inputs as well, giving you more creative flexibility.",
    },
    {
      id: 'faq-6',
      question: 'How long does it take to generate an AI video?',
      answer:
        'Most videos created with FalcoCut take only a few minutes to generate. The exact time depends on your prompt, the model you choose, and the length of the clip. Shorter videos usually finish faster, while more detailed scenes may take a bit longer. Even so, the process is much quicker than traditional editing or filming, letting you create multiple versions in a short session.',
    },
    {
      id: 'faq-7',
      question: 'Is there a free AI video generator?',
      answer:
        "Many AI video tools offer free plans or trial options, and FalcoCut also provides a way to test the platform before upgrading. Free versions usually include basic features so you can try text-to-video or image-to-video generation. They're a good way to explore the workflow, understand how prompts work, and see if the tool fits your needs before choosing a paid plan.",
    },
    {
      id: 'faq-8',
      question: 'How do I write prompts for an AI video generator?',
      answer:
        'Good prompts are clear and specific. Describe the subject, the setting, and the type of motion you want. For example, "A slow camera move across a sunny beach with gentle waves." Adding small details helps the AI understand your vision. You don\'t need to be technical—simple, direct language works best. If you\'re unsure, start with a short idea and refine it after seeing the first result.',
    },
    {
      id: 'faq-9',
      question: 'Do I need to download any software?',
      answer:
        "FalcoCut works directly in your browser, so you don't need to install anything to start creating videos. You can access the tool on most devices, including laptops and mobile browsers. This makes it easy to create videos anywhere without worrying about storage, updates, or system requirements.",
    },
    {
      id: 'faq-10',
      question: 'How secure is my data?',
      answer:
        'FalcoCut is built with strong privacy and security standards to protect your content. Your inputs, assets, and generated videos are handled according to strict data-protection practices. The platform uses secure systems to keep your files safe while you work. You stay in control of your projects, and your data is not shared without your permission.',
    },
  ];

  const handleItemClick = (id: string) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  return (
    <section className="w-full bg-white py-12 pad:py-16 sm:py-20 px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* 标题 */}
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-[#0F1117] text-center mb-10 pad:mb-12 sm:mb-16">
          Frequently Asked Questions
        </h2>

        {/* FAQ 列表 - 840px 宽度居中 */}
        <div className="max-w-[840px] mx-auto flex flex-col gap-2">
          {faqItems.map((item) => (
            <FAQItemComponent
              key={item.id}
              item={item}
              isExpanded={expandedIds.has(item.id)}
              onClick={() => handleItemClick(item.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
